import { useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, imgSrc } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { UserAvatar } from "@/components/UserAvatar";
import { ImageUpload } from "@/components/ImageUpload";
import { VideoAttach } from "@/components/VideoAttach";

const MAX_PHOTOS = 4;

const PhotoPreviews = ({ images, onRemove }) => (
  <div className="flex flex-wrap gap-2" data-testid="composer-photo-previews">
    {images.map((url, i) => (
      <div key={url} className="relative">
        <img src={imgSrc(url)} alt="" className="w-20 h-20 object-cover rounded-xl border border-[var(--hb-line)]" />
        <button type="button" onClick={() => onRemove(i)} data-testid={`composer-photo-remove-${i}`}
          className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-[var(--hb-ink)] text-white grid place-items-center">
          <X size={12} />
        </button>
      </div>
    ))}
  </div>
);

export const Composer = ({ categories, onPosted, groupId = "" }) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [images, setImages] = useState([]);
  const [video, setVideo] = useState("");
  const [category, setCategory] = useState("General");
  const [busy, setBusy] = useState(false);
  const allowed = categories.filter((c) => c !== "Announcements" || user.role === "admin");

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/posts", { title, body, category, images, video_url: video, group_id: groupId });
      onPosted(data);
      setTitle("");
      setBody("");
      setImages([]);
      setVideo("");
      setOpen(false);
      toast.success("Posted!");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="hb-card w-full flex items-center gap-3 p-4 text-left hover:border-[var(--hb-ink)] transition-colors"
        data-testid="composer-open"
      >
        <UserAvatar user={user} size={38} />
        <span className="text-[var(--hb-muted)]">Write something, boo...</span>
      </button>
    );
  }

  return (
    <div className="hb-card p-5 space-y-3 rise" data-testid="composer">
      <input className="hb-input font-display text-lg" placeholder="Title (optional)" value={title}
        onChange={(e) => setTitle(e.target.value)} data-testid="composer-title-input" />
      <textarea className="hb-input min-h-[120px] resize-y" placeholder="Share a win, ask for advice, find a collab..."
        value={body} onChange={(e) => setBody(e.target.value)} autoFocus data-testid="composer-body-input" />
      {images.length > 0 && <PhotoPreviews images={images} onRemove={(i) => setImages(images.filter((_, j) => j !== i))} />}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          <select className="hb-input !w-auto !py-2" value={category} onChange={(e) => setCategory(e.target.value)}
            data-testid="composer-category-select">
            {allowed.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          {images.length < MAX_PHOTOS && (
            <ImageUpload testId="composer-photo-upload" label="Photo" maxFiles={MAX_PHOTOS - images.length}
              onUploaded={(url) => setImages((prev) => [...new Set([...prev, url])].slice(0, MAX_PHOTOS))} />
          )}
          {user.role === "admin" && <VideoAttach value={video} onChange={setVideo} />}
        </div>
        <div className="flex gap-2">
          <button className="btn-ghost" onClick={() => setOpen(false)} data-testid="composer-cancel">Cancel</button>
          <button className="btn-primary" disabled={busy || (!body.trim() && images.length === 0 && !video)} onClick={submit} data-testid="composer-submit">
            {busy ? "Posting..." : "Post"}
          </button>
        </div>
      </div>
    </div>
  );
};
